import React from "react";
import {
  Box,
  Typography,
  Container,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { Images } from "../../utils/Images";
import DonateButton from "../../components/common/DonateButton";
import { DonateButtonWithArrowVariantTwo } from "../../components/common/Button/Button";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCmsHomeAction } from "../../store/slices/cmsSlice";
import NewsLogoSection from "../HomePage/NewsLogoSection";
import FeaturedOnHeading from "../../components/common/FeaturedOnHeading";
import AwardsSection from "../HomePage/AwardsSection";

const AboutUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  React.useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const { heroTitle, heroDescription, heroImages, whoWeAreTitle, whoWeAreBaseDesc, whoWeAreSecondDesc, whoWeAreImage1, whoWeAreImage2, whoWeAreImage3, visionTitle, visionDesc1, visionDesc2, visionPoint1, visionPoint2, visionImage1, visionImage2, missionTitle, missionBaseDesc, missionSecondDesc, missionImage } = React.useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const sectionHero = data.find((s) => s?.id === 85);
    const relatedHero = Array.isArray(sectionHero?.relatedCMS) ? sectionHero.relatedCMS : [];
    const firstHero = relatedHero[0] || {};
    const secondHero = relatedHero[1] || {};
    const imgs = [
      firstHero?.image,
      firstHero?.image2,
      firstHero?.image3,
      secondHero?.image,
      secondHero?.image2,
    ].filter(Boolean);

    const whoWeAreSec = data.find((s) => s?.id === 86);
    const whoWeAreRel = Array.isArray(whoWeAreSec?.relatedCMS) ? whoWeAreSec.relatedCMS : [];
    const whoWeAreFirst = whoWeAreRel[0] || {};

    const visionSec = data.find((s) => s?.id === 87);
    const visionRel = Array.isArray(visionSec?.relatedCMS) ? visionSec.relatedCMS : [];
    const visionFirst = visionRel[0] || {};

    const missionSec = data.find((s) => s?.id === 88);
    const missionRel = Array.isArray(missionSec?.relatedCMS) ? missionSec.relatedCMS : [];
    const missionFirst = missionRel[0] || {};

    return {
      heroTitle: sectionHero?.title,
      heroDescription: sectionHero?.sub_title,
      heroImages: imgs,
      whoWeAreTitle: whoWeAreSec?.title,
      whoWeAreBaseDesc: whoWeAreSec?.description,
      whoWeAreSecondDesc: whoWeAreFirst?.description,
      whoWeAreImage1: whoWeAreFirst?.image,
      whoWeAreImage2: whoWeAreFirst?.image2,
      whoWeAreImage3: whoWeAreFirst?.image3,
      visionTitle: visionSec?.title,
      visionDesc1: visionSec?.description,
      visionDesc2: visionFirst?.description,
      visionPoint1: visionFirst?.title,
      visionPoint2: visionFirst?.sub_title,
      visionImage1: visionFirst?.image,
      visionImage2: visionFirst?.image2,
      missionTitle: missionSec?.title,
      missionBaseDesc: missionSec?.description,
      missionSecondDesc: missionFirst?.description,
      missionImage: missionFirst?.image,
    };
  }, [cmsHome]);

  const { strengthTitle, strengthItems } = React.useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const sec = data.find((s) => s?.id === 89);
    const rel = Array.isArray(sec?.relatedCMS) ? sec.relatedCMS : [];
    return {
      strengthTitle: sec?.title,
      strengthItems: rel.slice(0, 4).map((item) => ({
        title: item?.title,
        description: item?.description,
      })),
    };
  }, [cmsHome]);

  const handleDonateClick = () => {
    navigate("/campaigns"); // Navigate to explore campaigns page
  };
  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "About us", path: null },
        ]}
        disableMaxWidth
      />
      <Box sx={{ width: "100%", px: isLaptop ? "0" : "40px" }}>
        <Box
          sx={{
            display: "flex",
            marginBottom: isMobile ? "30px" : isTablet ? "40px" : "82px",
            flexDirection: "column",
            px: isLaptop ? "16px" : "0px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              maxWidth:  "800px",
              margin: "0 auto",
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 600,
                color: "#1D1D1B",
                mb: "14px",
                fontSize: "18px",
                lineHeight: 1.2,
                display: isMobile ? "none" : "block",
              }}
            >
              About Us
            </Typography>

            {heroTitle && (
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: "#1D1D1B",
                  fontSize: isMobile ? "18px" : isTablet ? "22px" : "42px",
                  lineHeight: isMobile ? "24px" : isTablet ? "normal" : "56px",
                  mb: isMobile ? "10px" : "14px",
                  maxWidth: isMobile ? "272px" : "800px",
                }}
              >
                {heroTitle.split("\n").map((line, idx) => (
                  <React.Fragment key={idx}>
                    {line}
                    {idx < heroTitle.split("\n").length - 1 && <br />}
                  </React.Fragment>
                ))}
              </Typography>
            )}

            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: "#4A4A49",
                fontSize: isMobile ? "12px" : "14px",
                lineHeight: "22.652px",
                maxWidth: isMobile ? "328px" : "482px",
              }}
            >
              {heroDescription}
            </Typography>
          </Box>
          <Box
            className="about-banner"
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              alignItems: "end",
              marginTop: isTablet ? "20px" : "-42px",
            }}
          >
            {heroImages[0] && (
              <Box className="about-first" sx={{ width: "254px" }}>
                <Box
                  component="img"
                  src={heroImages[0]}
                  alt="aboutBanner1"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    maskImage: `url(${Images.aboutBannerMask1})`,
                    maskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskImage: `url(${Images.aboutBannerMask1})`,
                    WebkitMaskSize: "100% 100%",
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                  }}
                ></Box>
              </Box>
            )}
            {heroImages[1] && (
              <Box
                className="about-second"
                sx={{ width: "254px", display: isMobile ? "none" : "block" }}
              >
                <Box
                  component="img"
                  src={heroImages[1]}
                  alt="aboutBanner2"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    maskImage: `url(${Images.aboutBannerMask2})`,
                    maskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskImage: `url(${Images.aboutBannerMask2})`,
                    WebkitMaskSize: "100% 100%",
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                  }}
                ></Box>
              </Box>
            )}
            {heroImages[2] && (
              <Box className="about-third" sx={{ width: "303px" }}>
                <Box
                  component="img"
                  src={heroImages[2]}
                  alt="aboutBanner3"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    maskImage: `url(${Images.aboutBannerMask3})`,
                    maskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskImage: `url(${Images.aboutBannerMask3})`,
                    WebkitMaskSize: "100% 100%",
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                  }}
                ></Box>
              </Box>
            )}
            {heroImages[3] && (
              <Box
                className="about-second"
                sx={{ width: "254px", display: isMobile ? "none" : "block" }}
              >
                <Box
                  component="img"
                  src={heroImages[3]}
                  alt="aboutBanner4"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    maskImage: `url(${Images.aboutBannerMask4})`,
                    maskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskImage: `url(${Images.aboutBannerMask4})`,
                    WebkitMaskSize: "100% 100%",
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                  }}
                ></Box>
              </Box>
            )}
            {heroImages[4] && (
              <Box className="about-first" sx={{ width: "254px" }}>
                <Box
                  component="img"
                  src={heroImages[4]}
                  alt="aboutBanner5"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    maskImage: `url(${Images.aboutBannerMask5})`,
                    maskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    maskPosition: "center",
                    WebkitMaskImage: `url(${Images.aboutBannerMask5})`,
                    WebkitMaskSize: "100% 100%",
                    WebkitMaskRepeat: "no-repeat",
                    WebkitMaskPosition: "center",
                  }}
                ></Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: "#1D1D1B",
            display: "flex",
            flexDirection: isTablet ? "column-reverse" : "row",
            gap: isLaptop ? "50px" : "17px",
            padding: isLaptop ? "30px 16px 30px 16px " : "90px 0px 70px 70px",
            borderRadius: isTablet ? "0" : "30px",
            justifyContent: "space-evenly",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              maxWidth: "667px",
              "@media (max-width: 1200px)": {
                maxWidth: "667px",
                width: "100%",
              },
              "@media (max-width: 1100px)": {
                maxWidth: "467px",
                width: "100%",
              },
              "@media (max-width: 900px)": {
                maxWidth: "100%",
                width: "100%",
              },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? "16px" : isLaptop ? "18px" : "20px",
                lineHeight: isMobile ? "20px" : "42px",
                color: "#A9C74E",
                mb: isTablet ? "10px" : "30px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span>
                {" "}
                <img src={Images.aboutPeople} alt="Images.aboutPeople" />{" "}
              </span>
              Who we are
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? "18px" : isLaptop ? "22px" : "36px",
                lineHeight: isMobile ? "28px" : isLaptop ? "40px" : "50px",
                color: "#FFF",
                mb: "20px",
                maxWidth:isMobile ? "300px" :  "620px",
                textTransform: "capitalize",
              }}
            >
              {whoWeAreTitle}
            </Typography>
            <Typography
              variant="p"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "24px",
                color: "#E8E8E8",
                paddingBottom: isTablet ? "12px" : "20px",
                display: "block",
                maxWidth: isTablet ? "100%" : "667px",
              }}
            >
              {whoWeAreBaseDesc}
            </Typography>
            <Typography
              variant="p"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "24px",
                color: "#E8E8E8",
                display: "block",
                maxWidth: isTablet ? "100%" : "667px",
                paddingBottom: isTablet ? "20px" : "30px",
              }}
            >
              {whoWeAreSecondDesc}
            </Typography>
            <Box
              item
              sx={{
                textAlign: isTablet ? "center" : "left",
                display: "flex",
                justifyContent: isMobile ? "center" : "start",
              }}
            >
              <DonateButtonWithArrowVariantTwo
                onClick={handleDonateClick}
                text="Donate Now"
              />
            </Box>
          </Box>
          <Box>
            {/* Three Mask Images */}
            <Box
              sx={{
                flex: "1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {/* Three Mask Images */}
              <Box
                sx={{
                  width: "505px",
                  height: "425px",
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  "@media (max-width: 1300px)": {
                    width: "400px",
                    height: "420px",
                  },
                  "@media (max-width: 1100px)": {
                    width: "300px",
                    height: "380px",
                  },
                  "@media (max-width: 900px)": {
                    width: "400px",
                    height: "340px",
                  },
                  "@media (max-width: 768px)": {
                    width: "350px",
                    height: "300px",
                  },
                  "@media (max-width: 600px)": {
                    width: "274px",
                    height: "231px",
                    marginLeft: "50px",
                  },
                }}
              >
                {/* pink-mask */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "265px",
                    height: "324px",
                    top: "50px",
                    left: "-130px",
                    zIndex: 3,
                    "@media (max-width: 1300px)": {
                      width: "260px",
                      height: "320px",
                      top: "40px",
                      left: "-130px",
                    },
                    "@media (max-width: 1100px)": {
                      width: "235px",
                      height: "290px",
                      top: "35px",
                      left: "-130px",
                    },
                    "@media (max-width: 900px)": {
                      width: "210px",
                      height: "260px",
                      top: "30px",
                      left: "-100px",
                    },
                    "@media (max-width: 768px)": {
                      width: "185px",
                      height: "230px",
                      top: "25px",
                      left: "-80px",
                    },
                    "@media (max-width: 600px)": {
                      width: "144px",
                      height: "176px",
                      top: "20px",
                      left: "-60px",
                    },
                  }}
                >
                  {/* Pink Splash - Top Left Corner */}
                  <Box
                    component="img"
                    src={Images.pinkSplash}
                    alt="Pink Splash Decoration"
                    sx={{
                      position: "absolute",
                      top: "-91px",
                      left: "23px",
                      width: "358px",
                      height: "344px",
                      zIndex: 0,
                      "@media (max-width: 1300px)": {
                        top: "-53px",
                        left: "28px",
                        width: "350px",
                        height: "330px",
                      },
                      "@media (max-width: 1100px)": {
                        top: "-39px",
                        left: "34px",
                        width: "300px",
                        height: "280px",
                      },
                      "@media (max-width: 900px)": {
                        top: "-49px",
                        left: "17px",
                        width: "250px",
                        height: "220px",
                      },
                      "@media (max-width: 768px)": {
                        top: "-50px",
                        left: "-8px",
                        width: "250px",
                        height: "220px",
                      },
                      "@media (max-width: 600px)": {
                        top: "-11px",
                        left: "30px",
                        width: "60px",
                        height: "60px",
                      },
                    }}
                  />

                  <Box
                    component="img"
                    src={Images.pinkCircles}
                    alt="Pink Circles Decoration"
                    sx={{
                      position: "absolute",
                      bottom: "-28px",
                      left: "112px",
                      width: "81px",
                      height: "89px",
                      zIndex: 0,
                      "@media (max-width: 1300px)": {
                        bottom: "12px",
                        left: "108px",
                        width: "81px",
                        height: "80px",
                      },
                      "@media (max-width: 1100px)": {
                        bottom: "10px",
                        left: "113px",
                        width: "71px",
                        height: "70px",
                      },
                      "@media (max-width: 900px)": {
                        bottom: "-17px",
                        left: "84px",
                        width: "71px",
                        height: "70px",
                      },
                      "@media (max-width: 768px)": {
                        bottom: "-24px",
                        left: "63px",
                        width: "71px",
                        height: "70px",
                      },
                      "@media (max-width: 600px)": {
                        bottom: "-6px",
                        left: "52px",
                        width: "45px",
                        height: "45px",
                      },
                    }}
                  />
                </Box>
                {/* First Mask Image with Content */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "265px",
                    height: "324px",
                    top: "50px",
                    left: "0px",
                    zIndex: 3,
                    "@media (max-width: 1300px)": {
                      width: "200px",
                      height: "320px",
                      top: "40px",
                      left: "0px",
                    },
                    "@media (max-width: 1100px)": {
                      width: "180px",
                      height: "290px",
                      top: "35px",
                      left: "0px",
                    },
                    "@media (max-width: 900px)": {
                      width: "210px",
                      height: "260px",
                      top: "30px",
                      left: "0px",
                    },
                    "@media (max-width: 768px)": {
                      width: "185px",
                      height: "230px",
                      top: "25px",
                      left: "0px",
                    },
                    "@media (max-width: 600px)": {
                      width: "144px",
                      height: "176px",
                      top: "20px",
                      left: "0px",
                    },
                  }}
                >
                  {whoWeAreImage1 && (
                    <Box
                      component="img"
                      src={whoWeAreImage1}
                      alt="Who We Are Image 1"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        maskImage: `url(${Images.donateMonthlyMask1})`,
                        maskSize: "100% 100%",
                        maskRepeat: "no-repeat",
                        maskPosition: "center",
                        WebkitMaskImage: `url(${Images.donateMonthlyMask1})`,
                        WebkitMaskSize: "100% 100%",
                        WebkitMaskRepeat: "no-repeat",
                        WebkitMaskPosition: "center",
                      }}
                    />
                  )}
                </Box>

                {/* Second Mask Image */}
                {whoWeAreImage2 && (
                  <Box
                    sx={{
                      position: "absolute",
                      width: "187px",
                      height: "139px",
                      top: "84px",
                      right: "110px",
                      zIndex: 2,
                      "@media (max-width: 1300px)": {
                        width: "140px",
                        height: "125px",
                        top: "93px",
                        right: "101px",
                      },
                      "@media (max-width: 1100px)": {
                        width: "120px",
                        height: "125px",
                        top: "79px",
                        right: "36px",
                      },
                      "@media (max-width: 900px)": {
                        width: "195px",
                        height: "105px",
                        top: "61px",
                        right: "65px",
                      },
                      "@media (max-width: 768px)": {
                        width: "170px",
                        height: "95px",
                        top: "52px",
                        right: "55px",
                      },
                      "@media (max-width: 600px)": {
                        width: "100px",
                        height: "74px",
                        top: "39px",
                        right: "61px",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={whoWeAreImage2}
                      alt="Who We Are Image 2"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        maskImage: `url(${Images.donateMonthlyMask2})`,
                        maskSize: "100% 100%",
                        maskRepeat: "no-repeat",
                        maskPosition: "center",
                        WebkitMaskImage: `url(${Images.donateMonthlyMask2})`,
                        WebkitMaskSize: "100% 100%",
                        WebkitMaskRepeat: "no-repeat",
                        WebkitMaskPosition: "center",
                      }}
                    />
                  </Box>
                )}

                {/* Third Mask Image */}
                {whoWeAreImage3 && (
                  <Box
                    sx={{
                      position: "absolute",
                      width: "240px",
                      height: "155px",
                      bottom: "-13px",
                      left: "146px",
                      zIndex: 1,
                      "@media (max-width: 1300px)": {
                        width: "180px",
                        height: "180px",
                        bottom: "17px",
                        left: "112px",
                      },
                      "@media (max-width: 1100px)": {
                        width: "165px",
                        height: "165px",
                        bottom: "15px",
                        left: "102px",
                      },
                      "@media (max-width: 900px)": {
                        width: "145px",
                        height: "145px",
                        bottom: "2px",
                        left: "120px",
                      },
                      "@media (max-width: 768px)": {
                        width: "125px",
                        height: "125px",
                        bottom: "5px",
                        left: "105px",
                      },
                      "@media (max-width: 600px)": {
                        width: "130px",
                        height: "84px",
                        bottom: "1px",
                        left: "80px",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={whoWeAreImage3}
                      alt="Who We Are Image 3"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        maskImage: `url(${Images.donateMonthlyMask3})`,
                        maskSize: "100% 100%",
                        maskRepeat: "no-repeat",
                        maskPosition: "center",
                        WebkitMaskImage: `url(${Images.donateMonthlyMask3})`,
                        WebkitMaskSize: "100% 100%",
                        WebkitMaskRepeat: "no-repeat",
                        WebkitMaskPosition: "center",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: isTablet ? "column" : "row",
            gap: isMobile ? "0px" : "40px",
            borderRadius: isTablet ? "0" : "30px",
            alignItems: "center",
            marginTop: "80px",
            justifyContent: "space-evenly",
          }}
        >
          <Box>
            <Box
              sx={{
                flex: "1",
                display: "grid",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobile ? "16px" : "30px",
                px: isTablet ? "10px" : "0px",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                },
                "@media (max-width: 1400px)": {
                  px: "16px",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isMobile ? "10px" : "20px",
                }}
              >
                <Box
                  sx={{
                    width: "307px",
                    height: "304px",
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "30px",
                    overflow: "hidden",
                    "@media (max-width: 1300px)": {
                      width: "100%",
                      height: "304px",
                    },
                    "@media (max-width: 1100px)": {
                      width: "100%",
                      height: "304px",
                    },
                    "@media (max-width: 900px)": {
                      width: "100%",
                      height: "304px",
                    },
                    "@media (max-width: 768px)": {
                      width: "100%",
                      height: "172px",
                    },
                    "@media (max-width: 600px)": {
                      width: "100%",
                      height: "172px",
                    },
                  }}
                >
                  {/* First Mask Image with Content */}
                  {visionImage1 && (
                    <Box
                      component="img"
                      src={visionImage1}
                      alt="Vision Image 1"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    border: " 1px solid rgba(0, 0, 0, 0.10)",
                    borderRadius: isMobile ? "16px" : "30px",
                    height: isMobile ? "102px" : "181px",
                    paddingInline: isMobile ? "16px" : "33px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: isMobile ? "16px" : isLaptop ? "18px" : "42px",
                      lineHeight: isMobile ? "20px" : "42px",
                      color: "#1D1D1B",
                      mb: isTablet ? "10px" : "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {visionPoint1}
                  </Typography>
                  <Typography
                    variant="p"
                    sx={{
                      fontWeight: 600,
                      fontSize: isMobile ? "12px" : isLaptop ? "18px" : "18px",
                      lineHeight: isMobile ? "20px" : "29px",
                      color: "#979796",
                      mb: isTablet ? "10px" : "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    Communities Reached
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isMobile ? "10px" : "20px",
                }}
              >
                <Box
                  sx={{
                    border: " 1px solid rgba(0, 0, 0, 0.10)",
                    borderRadius: isMobile ? "16px" : "30px",
                    height: isMobile ? "102px" : "181px",
                    paddingInline: isMobile ? "16px" : "33px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: isMobile ? "16px" : isLaptop ? "18px" : "42px",
                      lineHeight: isMobile ? "20px" : "42px",
                      color: "#1D1D1B",
                      mb: isTablet ? "10px" : "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    {visionPoint2}
                  </Typography>
                  <Typography
                    variant="p"
                    sx={{
                      fontWeight: 600,
                      fontSize: isMobile ? "12px" : isLaptop ? "18px" : "18px",
                      lineHeight: isMobile ? "20px" : "29px",
                      color: "#979796",
                      mb: isTablet ? "10px" : "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    Communities Reached
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "307px",
                    height: "304px",
                    position: "relative",
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "30px",
                    "@media (max-width: 1300px)": {
                      width: "100%",
                      height: "304px",
                    },
                    "@media (max-width: 1100px)": {
                      width: "100%",
                      height: "304px",
                    },
                    "@media (max-width: 900px)": {
                      width: "100%",
                      height: "304px",
                    },
                    "@media (max-width: 768px)": {
                      width: "100%",
                      height: "172px",
                    },
                    "@media (max-width: 600px)": {
                      width: "100%",
                      height: "172px",
                    },
                  }}
                >
                  {/* First Mask Image with Content */}
                  {visionImage2 && (
                    <Box
                      component="img"
                      src={visionImage2}
                      alt="Vision Image 2"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ padding: isLaptop ? "30px 16px 30px 16px " : "0" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? "16px" : isLaptop ? "18px" : "20px",
                lineHeight: isMobile ? "20px" : "42px",
                color: "#A9C74E",
                mb: isTablet ? "10px" : "30px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span>
                {" "}
                <img src={Images.aboutPeople} alt="Images.aboutPeople" />{" "}
              </span>
              Our Vision
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: isMobile ? "18px" : isLaptop ? "22px" : "36px",
                lineHeight: isMobile ? "28px" : isLaptop ? "40px" : "50px",
                color: "#1D1D1B",
                mb: "20px",
                maxWidth: isMobile ? "328px" : "577px",
                textTransform: "capitalize",
              }}
            >
              {visionTitle}
            </Typography>
            <Typography
              variant="p"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "24px",
                color: "#686866",
                paddingBottom: isTablet ? "12px" : "20px",
                display: "block",
                maxWidth: isTablet ? "100%" : "667px",
              }}
            >
              {visionDesc1}
            </Typography>
            <Typography
              variant="p"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "24px",
                color: "#686866",
                display: "block",
                maxWidth: isTablet ? "100%" : "667px",
                paddingBottom: isTablet ? "20px" : "30px",
              }}
            >
              {visionDesc2}
            </Typography>
            <Box
              item
              sx={{
                textAlign: isTablet ? "center" : "left",
                display: "flex",
                justifyContent: isMobile ? "center" : "start",
              }}
            >
              <DonateButtonWithArrowVariantTwo
                onClick={handleDonateClick}
                text="Donate Now"
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: "#1D1D1B",
            display: "flex",
            flexDirection: isTablet ? "column-reverse" : "row",
            gap: isMobile ? "20px" : "119px",
            padding: isLaptop ? "30px 16px 30px 16px " : "90px 0px 70px 70px",
            borderRadius: isTablet ? "0" : "30px",
            justifyContent: "space-evenly",
            alignItems: "center",
            marginTop: isMobile ? "0px" : "70px",
            "@media (max-width: 1400px)": {
              gap: "80px",
            },
            "@media (max-width: 1200px)": {
              gap: "189px",
            },
            "@media (max-width: 899px)": {
              gap: "0px",
            },
          }}
        >
          <Box
            sx={{
              maxWidth: "667px",
              "@media (max-width: 1200px)": {
                maxWidth: "667px",
                width: "100%",
              },
              "@media (max-width: 1100px)": {
                maxWidth: "467px",
                width: "100%",
              },
              "@media (max-width: 900px)": {
                maxWidth: "100%",
                width: "100%",
              },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? "16px" : isLaptop ? "18px" : "20px",
                lineHeight: isMobile ? "20px" : "42px",
                color: "#A9C74E",
                mb: isTablet ? "10px" : "30px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span>
                {" "}
                <img src={Images.aboutPeople} alt="Images.aboutPeople" />{" "}
              </span>
              Our Mission
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: isMobile ? "18px" : isLaptop ? "22px" : "36px",
                lineHeight: isMobile ? "28px" : isLaptop ? "40px" : "50px",
                color: "#FFF",
                mb: "20px",
                maxWidth: "577px",
                textTransform: "capitalize",
              }}
            >
              {missionTitle}
            </Typography>
            <Typography
              variant="p"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "24px",
                color: "#E8E8E8",
                paddingBottom: isTablet ? "12px" : "20px",
                display: "block",
                maxWidth: isMobile ? "100%" : "667px",
              }}
            >
              {missionBaseDesc}
            </Typography>
            <Typography
              variant="p"
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "24px",
                color: "#E8E8E8",
                display: "block",
                maxWidth: isTablet ? "100%" : "667px",
                paddingBottom: isTablet ? "20px" : "30px",
              }}
            >
              {missionSecondDesc}
            </Typography>
            <Box
              item
              sx={{
                textAlign: isTablet ? "center" : "left",
                display: "flex",
                justifyContent: isMobile ? "center" : "start",
              }}
            >
              <DonateButtonWithArrowVariantTwo
                onClick={handleDonateClick}
                text="Donate Now"
              />
            </Box>
          </Box>
          <Box>
            <Box
              sx={{
                flex: "1",
                display: isTablet ? "none" : "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Box>
                {/* pink-mask */}
                <Box
                  sx={{
                    position: "absolute",
                    width: "265px",
                    height: "324px",
                    top: "-20px",
                    left: "-160px",
                    transform: "rotate(-25deg)",
                    zIndex: 2,
                    "@media (max-width: 1300px)": {
                      width: "260px",
                      height: "320px",
                      top: "40px",
                      left: "-130px",
                    },
                    "@media (max-width: 1100px)": {
                      width: "235px",
                      height: "290px",
                      top: "35px",
                      left: "-130px",
                    },
                    "@media (max-width: 900px)": {
                      width: "210px",
                      height: "260px",
                      top: "30px",
                      left: "-100px",
                    },
                    "@media (max-width: 768px)": {
                      width: "185px",
                      height: "230px",
                      top: "25px",
                      left: "-80px",
                    },
                    "@media (max-width: 600px)": {
                      width: "144px",
                      height: "176px",
                      top: "20px",
                      left: "-60px",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={Images.pinkMask}
                    alt="Images.pinkMask"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      maskImage: `url(${Images.pinkMask})`,
                      maskSize: "100% 100%",
                      maskRepeat: "no-repeat",
                      maskPosition: "center",
                      WebkitMaskImage: `url(${Images.pinkMask})`,
                      WebkitMaskSize: "100% 100%",
                      WebkitMaskRepeat: "no-repeat",
                      WebkitMaskPosition: "center",
                    }}
                  />
                </Box>
                {/* First Mask Image with Content */}
                <Box
                  sx={{
                    width: "393px",
                    height: "480px",
                    borderRadius: "20px",
                    overflow: "hidden",
                    zIndex: 3,
                    position: "relative",
                    "@media (max-width: 1300px)": {
                      width: "393px",
                      height: "393px",
                      top: "40px",
                      left: "0px",
                    },
                    "@media (max-width: 1100px)": {
                      width: "303px",
                      height: "303px",
                      top: "35px",
                      left: "0px",
                    },
                    "@media (max-width: 900px)": {
                      width: "210px",
                      height: "260px",
                      top: "30px",
                      left: "0px",
                    },
                    "@media (max-width: 768px)": {
                      width: "185px",
                      height: "230px",
                      top: "25px",
                      left: "0px",
                    },
                    "@media (max-width: 600px)": {
                      width: "144px",
                      height: "176px",
                      top: "20px",
                      left: "0px",
                    },
                  }}
                >
                  {missionImage && (
                    <Box
                      component="img"
                      src={missionImage}
                      alt="Our Mission Image"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: "70px", mb: isMobile ? "20px" : "70px", px: isLaptop ? "16px" : "0px" }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              fontSize: isMobile ? "16px" : isLaptop ? "18px" : "20px",
              lineHeight: isMobile ? "20px" : "42px",
              color: "#A9C74E",
              mb: isTablet ? "10px" : "30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              textAlign: "center",
            }}
          >
            <span>
              {" "}
              <img src={Images.aboutPeople} alt="Images.aboutPeople" />{" "}
            </span>
            Our Strength
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: isMobile ? "18px" : isLaptop ? "22px" : "36px",
              lineHeight: isMobile ? "28px" : isLaptop ? "40px" : "50px",
              color: "#1D1D1B",
              mb: "20px",
              maxWidth:isMobile ? "307px" :  "620px",
              textAlign: "center",
              mx: "auto",
              textTransform: "capitalize",
            }}
          >
            {strengthTitle}
          </Typography>
          <Box
            sx={{
              display: "grid",
              justifyContent: "center",
              gap: "18px",
              mt: isMobile ? "24px" : "40px",
              flexWrap: isTablet ? "wrap" : "no-wrap",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
            }}
          >
            {strengthItems.map((item, idx) => {
              const icons = [
                Images.strengthGrid1,
                Images.strengthGrid2,
                Images.strengthGrid3,
                Images.strengthGrid4,
              ];
              const icon = icons[idx];
              return (
                <Box
                  key={idx}
                  sx={{
                    border: " 0.836px solid #DEDEDE",
                    display: "flex",
                    alignItems: isMobile ? "center" : "self-start",
                    textAlign: isMobile ? "center" : "left",
                    flexDirection: "column",
                    borderRadius: "13.37px",
                    padding: "20px",
                    flex: "1",
                  }}
                >
                  {icon && (
                    <Box
                      sx={{
                        width: isMobile ? "35px" : "50px",
                        height: isMobile ? "36px" : "50px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#A9C74E",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mb: "20px",
                      }}
                    >
                      <Box component="img" sx={{ width: isMobile ? "20px" : "auto" }} src={icon} alt="strength icon" />
                    </Box>
                  )}
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      fontSize: isMobile ? "14px" : isLaptop ? "14px" : "16px",
                      lineHeight: isMobile ? "14px" : isLaptop ? "16px" : "16px",
                      color: "#131313",
                      mb: "10px",
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="p"
                    sx={{
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "22px",
                      color: "#686866",
                      opacity: 0.8,
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        {/* News Logo Section */}
        <Box>
          <AwardsSection />
          <FeaturedOnHeading />
          <NewsLogoSection />
        </Box>
      </Box>
    </Box>
  );
};

export default AboutUs;
